import { useMemo } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { teamOnSide, type Round, type SideIndex } from '@flow/core';
import { useDeps } from '../../app/services';

export interface Speaker {
  /** Ballot key: team letter + speaker index ("A0", "B1"). */
  key: string;
  team: 'A' | 'B';
  index: number;
  /** Debater's name if entered (device-only), else "Lincoln AB speaker 1". */
  label: string;
  code: string;
  side: SideIndex | null;
}

/** Every debater in the round, in ballot order (team A then B). */
export const useSpeakers = (round: Round): Speaker[] => {
  const deps = useDeps();
  const names = useLiveQuery(() => deps.names.get(round.id), [round.id]);
  return useMemo(
    () => (['A', 'B'] as const).flatMap((team) => Array.from({ length: round.format.teamSize }, (_, i) => {
      const name = (team === 'A' ? names?.teamA : names?.teamB)?.[i]?.trim();
      const code = team === 'A' ? round.teamA.code : round.teamB.code;
      const side = round.teamASide === null ? null : (([0, 1] as SideIndex[]).find((s) => teamOnSide(round, s) === team) ?? null);
      return { key: `${team}${i}`, team, index: i, code, side, label: name || `${code} speaker ${i + 1}` };
    })),
    [names, round],
  );
};
